/**
 * What the AI assistants layer adds to a Momentum step card: the "…" menu with
 * "Hand off to your assistant" (AI 4), a note dialog, and the chips that show what an
 * assistant did ("Planned with Claude · note added", AI 6/9/12/19/22).
 */
import { useEffect, useState } from "react";
import { CalendarCheck, MoreHorizontal, Pencil, Send, Sparkle } from "lucide-react";
import { ASSISTANTS, ASSISTANT_IDS, handoffUrl, scheduledChipLabel, stepMarkLabel, type AssistantId } from "@workspace/assistants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { useAuth } from "@/hooks/useAuth";
import { connectionFor, useAssistantsOverview, useSaveStepNote, useStepMarks } from "@/hooks/use-assistants";
import { ASSISTANTS_COMING_SOON, AssistantChip, AssistantMark, INK, MUTED, PURPLE, PillButton, Tag } from "@/components/assistant/assistant-ui";
import type { MomentumItem } from "@/lib/momentum";
import { cn } from "@/lib/utils";

/**
 * The "…" button on a step card: a note, and handing the step to an assistant. Calendar
 * has one way in — the card's own "Add to Calendar" / date chip — so it isn't repeated here.
 */
export function StepMenu({ item }: { item: MomentumItem }) {
  const { isAuthenticated } = useAuth();
  const { data: overview } = useAssistantsOverview(isAuthenticated);
  const [noteOpen, setNoteOpen] = useState(false);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          aria-label={`More for: ${item.text}`}
          className={cn(
            "-mr-1.5 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-lg transition-[opacity,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            "text-[#76695f] hover:bg-[rgba(47,41,37,0.06)] data-[state=open]:bg-[rgba(47,41,37,0.06)]",
            // Shown on hover or focus where there's a pointer; always on touch screens.
            "opacity-100 md:opacity-0 md:group-hover/card:opacity-100 md:focus-visible:opacity-100 md:data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100",
          )}
        >
          <MoreHorizontal className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          // Above the phone Momentum sheet (z-61).
          className="z-[70] w-[300px] rounded-2xl border-[rgba(47,41,37,0.1)] bg-white p-1.5 shadow-[0_12px_16px_rgba(28,25,23,0.16)]"
        >
          <MenuRow icon={<Pencil />} title="Write a note" onSelect={() => setNoteOpen(true)} />
          <DropdownMenuSeparator className="mx-0 my-0.5 bg-[rgba(47,41,37,0.08)]" />
          <DropdownMenuLabel className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] ${MUTED}`}>
            Hand off to your assistant
            {ASSISTANTS_COMING_SOON && (
              <span className="normal-case tracking-normal">
                <Tag tone="purple">Coming soon</Tag>
              </span>
            )}
          </DropdownMenuLabel>
          {ASSISTANT_IDS.map((assistant) => {
            const name = ASSISTANTS[assistant].name;
            const status = connectionFor(overview, assistant)?.status;
            const ready = !ASSISTANTS_COMING_SOON && status === "connected";
            const why = ASSISTANTS_COMING_SOON ? `Connecting ${name} is on its way` : !overview?.included ? "Part of Plus and Premium" : status === "paused" ? "Paused" : `Connect ${name} first`;
            return (
              <MenuRow
                key={assistant}
                icon={<Send />}
                title={`Ask ${name} to help`}
                detail={ready ? `Opens ${name} with this step` : why}
                trailing={ready ? <AssistantMark assistant={assistant} size={22} /> : null}
                disabled={!ready}
                accent={ready}
                onSelect={() => window.open(handoffUrl(assistant, item.text), "_blank", "noopener,noreferrer")}
              />
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <StepNoteDialog item={item} open={noteOpen} onOpenChange={setNoteOpen} />
    </>
  );
}

function MenuRow({
  icon,
  title,
  detail,
  trailing,
  disabled = false,
  accent = false,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  detail?: string;
  trailing?: React.ReactNode;
  disabled?: boolean;
  accent?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        "items-start gap-[10px] rounded-[10px] px-3 py-2.5 focus:bg-[rgba(110,83,172,0.08)] data-[disabled]:opacity-55",
        "[&>svg]:mt-px [&>svg]:size-4",
        accent ? "text-[#6e53ac] focus:text-[#6e53ac]" : `${INK} focus:text-[#2f2925]`,
      )}
    >
      {icon}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        {detail && <span className={`text-xs ${MUTED}`}>{detail}</span>}
      </span>
      {trailing}
    </DropdownMenuItem>
  );
}

/** "Write a note": the same note as the "write" box under the step in its Unfolding. */
function StepNoteDialog({ item, open, onOpenChange }: { item: MomentumItem; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveStepNote();

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    setError(null);
    let cancelled = false;
    authFetch(apiUrl(`/journals/${item.journalId}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { actions?: Array<{ id: string; notes?: string }> } | null) => {
        if (cancelled) return;
        setText(j?.actions?.find((a) => a.id === item.id)?.notes ?? "");
        setLoaded(true);
      })
      .catch(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [open, item.id, item.journalId]);

  async function submit() {
    try {
      await save.mutateAsync({ journalId: item.journalId, stepId: item.id, notes: text });
      onOpenChange(false);
    } catch {
      setError("Couldn't save your note. Try again in a moment.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="z-[100] bg-[rgba(47,41,37,0.28)]" className="z-[101] max-w-[480px] gap-4 rounded-[28px] border-0 bg-white p-6 sm:rounded-[28px] sm:p-7">
        <DialogTitle className={`pr-6 text-lg font-semibold leading-snug ${INK}`}>{item.text}</DialogTitle>
        <DialogDescription className={`-mt-2 text-[13px] ${MUTED}`}>A note on this step. It shows under the step in its Unfolding too.</DialogDescription>
        <textarea
          autoFocus
          value={text}
          disabled={!loaded}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder={loaded ? "Write your note…" : "Loading…"}
          className={`w-full resize-none rounded-2xl border border-[rgba(47,41,37,0.12)] bg-[#f8f6f2] p-3.5 text-sm leading-relaxed ${INK} focus:outline-none focus:ring-2 focus:ring-[#6e53ac]/30`}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-3">
          <PillButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </PillButton>
          <PillButton onClick={() => void submit()} disabled={!loaded || save.isPending}>
            {save.isPending ? "Saving…" : "Save note"}
          </PillButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The chips after "Add to Calendar": the time an assistant scheduled, and what it did.
 * With a note, the chip opens it (AI 22); on phones (`compact`) the note shows under it (AI 12).
 */
export function StepAssistantChips({
  item,
  compact = false,
  onCalendar = false,
}: {
  item: MomentumItem;
  compact?: boolean;
  /** The calendar layer already shows this step's date chip, so the assistant's time chip steps aside. */
  onCalendar?: boolean;
}) {
  const { isAuthenticated } = useAuth();
  const mark = useStepMarks(isAuthenticated).get(item.id);
  if (!mark) return null;
  const label = stepMarkLabel(mark, compact);

  return (
    <>
      {/* Covers an assistant-made event only until the calendar layer shows its own date chip for it. */}
      {mark.scheduled && !compact && !onCalendar && (
        <AssistantChip icon={<CalendarCheck />} aria-label={`Scheduled ${scheduledChipLabel(mark.scheduled.start)}`}>
          {scheduledChipLabel(mark.scheduled.start)}
        </AssistantChip>
      )}
      {mark.note && !compact ? (
        <Popover>
          <PopoverTrigger asChild>
            <AssistantChip icon={<Sparkle />}>{label}</AssistantChip>
          </PopoverTrigger>
          <NotePopover assistant={mark.assistant} note={mark.note} />
        </Popover>
      ) : (
        <AssistantChip icon={<Sparkle />} compact={compact}>
          {label}
        </AssistantChip>
      )}
      {compact && mark.note && <p className={`basis-full pl-0.5 text-xs leading-relaxed ${INK}`}>“{mark.note}”</p>}
    </>
  );
}

function NotePopover({ assistant, note }: { assistant: AssistantId; note: string }) {
  return (
    <PopoverContent align="start" className="z-[70] w-[330px] rounded-2xl border-[rgba(47,41,37,0.08)] bg-white p-4 shadow-[0_12px_16px_rgba(28,25,23,0.14)]">
      <p className={`flex items-center gap-1.5 text-xs font-semibold ${PURPLE}`}>
        <Pencil className="size-3.5" />
        Note · from {ASSISTANTS[assistant].name}
      </p>
      <p className={`mt-2 whitespace-pre-wrap text-sm leading-relaxed ${INK}`}>{note}</p>
    </PopoverContent>
  );
}
